import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { GameState, Book, CollectibleObject } from '../types';
import BookCard from './BookCard';
import SubscriptionModal from './Auth/SubscriptionModal';
import SubscriptionScreen from './SubscriptionScreen';
import ParentalPin from './ParentalPin';
import { useTranslation } from '../contexts/TranslationContext';
import { useUserProfile } from '../hooks/useUserProfile';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '../lib/supabaseClient';

// Hook pour détecter l'orientation
const useOrientation = () => {
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined' && window.innerWidth > window.innerHeight
  );

  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return isLandscape;
};

interface BooksScreenProps {
  gameState: GameState;
  books: Book[];
  freeBooks: Book[];
  allObjects: CollectibleObject[];
  onBack: () => void;
  onBookRead: (book: Book) => void;
  onTabChange: (tab: string) => void;
  onPremiumUpgrade: () => void;
  isGuestMode?: boolean;
  onRequestAuth?: () => void;
  onSubscribe?: () => void;
}

const BooksScreen: React.FC<BooksScreenProps> = ({
  gameState,
  books,
  freeBooks,
  allObjects,
  onBack,
  onBookRead,
  onTabChange,
  onPremiumUpgrade,
  isGuestMode = false,
  onRequestAuth,
  onSubscribe
}) => {
  const { activeTab, completedBooks, storyProgress } = gameState;
  const { t } = useTranslation();
  const { user, isAuthenticated, profile, authProvider } = useUserProfile();
  const { hasPremiumAccess } = useSubscription(user?.id);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionModalVariant, setSubscriptionModalVariant] = useState<'guest' | 'free-user'>('free-user');
  const [showPinVerification, setShowPinVerification] = useState(false);
  const [showFullPaywall, setShowFullPaywall] = useState(false);

  // Déterminer le statut premium depuis subscriptions table (source de vérité unique)
  const isPremium = isAuthenticated && hasPremiumAccess;

  // Si premium, afficher par défaut l'onglet "Tous les livres"
  React.useEffect(() => {
    if (isPremium && activeTab === 'apercu') {
      onTabChange('collection');
    }
  }, [isPremium, activeTab, onTabChange]);

  const handleTabClick = (tab: string) => {
    console.log('📚 Tab click:', { tab, isAuthenticated, isPremium, hasPremiumAccess });

    // Block access to 'collection' tab (all books) for non-premium users
    if (tab === 'collection') {
      // Guest users: ask to create account (popup modal)
      if (!isAuthenticated) {
        console.log('📚 Guest blocked - showing modal');
        setSubscriptionModalVariant('guest');
        setShowSubscriptionModal(true);
        return;
      }

      // Non-premium users: show PIN verification first, then fullscreen paywall
      if (!isPremium) {
        console.log('📚 Free user blocked - showing PIN then paywall');
        // If PIN is configured, show PIN verification first
        if (profile?.parentPinHash) {
          setShowPinVerification(true);
        } else {
          // No PIN configured, go directly to paywall
          setShowFullPaywall(true);
        }
        return;
      }
    }

    // Premium users or accessing 'apercu' tab: allow
    onTabChange(tab);
  };

  const handleBookClick = (book: Book) => {
    console.log('📚 Click sur livre:', {
      bookTitle: book.title,
      isPremium: book.premium,
      isAuthenticated,
      hasPremiumAccess
    });

    // Guest tries to read a premium book -> ask to create account
    if (book.premium && !isAuthenticated) {
      console.log('🚫 Guest bloqué sur livre premium');
      setSubscriptionModalVariant('guest');
      setShowSubscriptionModal(true);
      return;
    }

    // Free user tries to read a premium book -> ask to subscribe
    if (book.premium && isAuthenticated && !hasPremiumAccess) {
      console.log('🚫 Freemium bloqué sur livre premium');
      setSubscriptionModalVariant('free-user');
      setShowSubscriptionModal(true);
      return;
    }

    // Sinon, lecture normale
    console.log('✅ Lecture autorisée');
    onBookRead(book);
  };

  const isNative = Capacitor.isNativePlatform();
  const isLandscape = useOrientation();

  // Mode paysage natif uniquement
  const isNativeLandscape = isNative && isLandscape;

  // PIN verification screen (fullscreen)
  if (showPinVerification) {
    return (
      <ParentalPin
        mode="verify"
        currentPinHash={profile?.parentPinHash}
        userEmail={user?.email}
        authProvider={authProvider}
        title={t("books.parental_verification", "Parental verification")}
        subtitle={t("books.enter_your_pin_to_access_subscriptions", "Enter your PIN to access subscriptions")}
        onSuccess={() => {
          setShowPinVerification(false);
          setShowFullPaywall(true);
        }}
        onCancel={() => setShowPinVerification(false)}
      />
    );
  }

  // Full paywall screen (fullscreen)
  if (showFullPaywall) {
    return (
      <SubscriptionScreen
        onBack={() => setShowFullPaywall(false)}
        onSubscriptionComplete={async (plan) => {
          // Create subscription in database
          if (!user) return;

          const now = new Date();
          const trialEndsAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          const currentPeriodEnd = plan === 'yearly'
            ? new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
            : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

          const { error } = await supabase
            .from('subscriptions')
            .upsert({
              user_id: user.id,
              status: 'trial',
              tier: 'premium',
              plan: plan,
              trial_ends_at: trialEndsAt.toISOString(),
              current_period_start: now.toISOString(),
              current_period_end: currentPeriodEnd.toISOString(),
              provider: 'stripe',
            }, {
              onConflict: 'user_id'
            });

          if (error) {
            console.error('Error creating subscription:', error);
          }

          setShowFullPaywall(false);
          window.location.reload();
        }}
      />
    );
  }

  return (
    <div className={`bg-gray-50 ${isNative ? 'h-screen flex flex-col overflow-hidden' : 'h-screen-adaptive'}`}>
      {/* Header */}
      <div className={`bg-white shadow-sm ${isNative ? 'safe-area-top pt-2 flex-shrink-0' : ''}`}>
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ChevronLeft size={20} />
              {t("books.back", "Back")}
            </button>
            <h1 className={`font-bold text-gray-900 font-title ${isNative ? 'text-lg' : 'text-xl'}`}>📚 {t("books.our_adventures", "Our Adventures")}</h1>
          </div>
        </div>
      </div>

      {/* Navigation avec onglets */}
      <div className={`bg-white border-b ${isNative ? 'flex-shrink-0' : ''}`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex space-x-8">
            {/* Masquer l'onglet Aperçu si premium */}
            {!isPremium && (
              <button
                onClick={() => handleTabClick('apercu')}
                className={`py-3 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'apercu'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t("books.preview", "Preview")}
              </button>
            )}
            <button
              onClick={() => handleTabClick('collection')}
              className={`py-3 px-2 border-b-2 font-medium text-sm transition-colors relative ${
                activeTab === 'collection'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t("books.all_books", "All books")}
            </button>
          </div>
        </div>
      </div>

      {/* Contenu - scrollable sur mobile, normal sur web */}
      <div
        className={`mx-auto py-4 ${isNative ? (isNativeLandscape ? 'max-w-none px-4' : 'max-w-lg px-4') + ' flex-1 overflow-y-auto safe-area-bottom' : 'max-w-4xl px-4 py-6'}`}
        style={isNativeLandscape ? {
          paddingLeft: 'calc(1rem + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(1rem + env(safe-area-inset-right, 0px))'
        } : undefined}
      >
        {activeTab === 'apercu' ? (
          <div>
            <div className="mb-4">
              <h2 className={`font-semibold mb-1 font-title ${isNative ? 'text-lg' : 'text-xl'}`}>{t("books.books_of_the_week", "Books of the week")}</h2>
              <p className={`text-gray-600 ${isNative ? 'text-sm' : ''}`}>{t("books.discover_our_free_stories", "Discover our free stories")}</p>
            </div>

            {/* Aperçu: cartes rectangulaires empilées sur mobile portrait, grille en paysage natif */}
            <div className={`grid ${isNativeLandscape ? 'grid-cols-3 gap-3' : isNative ? 'grid-cols-1 gap-3' : 'grid-cols-1 gap-6'}`}>
              {freeBooks.map(book => (
                <BookCard
                  key={book.id}
                  book={book}
                  completed={completedBooks.includes(book.id)}
                  inProgress={storyProgress?.storyId === book.id}
                  allObjects={allObjects}
                  onRead={() => handleBookClick(book)}
                  showPrice={false}
                  viewMode={isNative ? 'preview-card' : 'list'}
                />
              ))}
            </div>
          </div>
        ) : (
          <div>
            <div className={`flex justify-between items-center mb-4 ${isNative ? 'flex-col gap-3' : 'mb-6'}`}>
              <h2 className={`font-semibold font-title ${isNative ? 'text-lg' : 'text-xl'}`}>{t("books.all_adventures", "All adventures")}</h2>
              {!isPremium && (
                <button
                  onClick={onPremiumUpgrade}
                  className={`bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all hover:scale-105 ${
                    isNative ? 'px-3 py-1.5 text-sm w-full' : 'px-4 py-2'
                  }`}
                >
                  {t("books.unlock_all", "Unlock all")}
                </button>
              )}
            </div>

            {/* Grid: 3 colonnes sur mobile portrait, 4 en paysage natif avec preview-card, liste sur web */}
            <div className={`grid ${isNativeLandscape ? 'grid-cols-4 gap-3' : isNative ? 'grid-cols-3 gap-3' : 'grid-cols-1 gap-6'}`}>
              {books.map(book => (
                <BookCard
                  key={book.id}
                  book={book}
                  completed={completedBooks.includes(book.id)}
                  inProgress={storyProgress?.storyId === book.id}
                  allObjects={allObjects}
                  onRead={() => handleBookClick(book)}
                  showPrice={true}
                  isPremium={isPremium}
                  viewMode={isNativeLandscape ? 'preview-card' : undefined}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <SubscriptionModal
          onClose={() => setShowSubscriptionModal(false)}
          variant={subscriptionModalVariant}
          onSignup={() => {
            setShowSubscriptionModal(false);
            if (onRequestAuth) onRequestAuth();
          }}
          onLogin={() => {
            setShowSubscriptionModal(false);
            if (onRequestAuth) onRequestAuth();
          }}
          onSubscribe={() => {
            setShowSubscriptionModal(false);
            if (onSubscribe) onSubscribe();
          }}
        />
      )}
    </div>
  );
};

export default BooksScreen;